import React, { FunctionComponent, useState } from 'react'
import { Button, Heading, InputMoney, Text } from '@afterpaytouch/core'
import { useTranslation } from '../../../../utils/convergence'
import { useMonitoringWithEnduringEventProperties } from '../../../../integrations/monitoring'
import { Features, mapLocaleToCurrency, TrackingEvent } from '../../../../model'
import { getValidationError } from './helper'
import {
  setUserHasApplePaylaterCard,
  setUserHasInstoreCard,
  useAppDispatch,
  useAppRouter,
  useCreateCheckoutMutation,
  useApplePayEntryData,
  useStartCheckout,
} from '../../../../state'
import { isNullOrUndefined, isUndefinedOrEmpty } from '@afterpay/utils'
import styles from './style.module.css'
import { ApplePaylaterFlowType } from '../../../../flow/apple-paylater/model'
import { getNextApplePaylaterRoute, parseHasInstoreCard } from '../../../../flow/apple-paylater/utils'
import { Route } from '../../../../config/router'
import { Country } from '@afterpay/types'
import { validateCreateApplePayCheckoutResponse } from '../../../../page/apple-paylater/entry/validation'
import { APPLE_PAYLATER_QUERY_PARAMS } from '../../../../flow/apple-paylater/queryParams'
import { MAX_DIGITS } from './constants'
import { useFlag } from '@afterpaytouch/feature'
import { usePostLoginRoute } from '../../../../page/apple-paylater/applePaylaterRouter'

export const AdjustAmount: FunctionComponent = () => {
  const { t } = useTranslation()
  const router = useAppRouter()
  const { locale } = router
  const [amount, setAmount] = useState('')
  const [error, setError] = useState<string | undefined>(undefined)
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const [createCheckout, { isLoading, isSuccess }] = useCreateCheckoutMutation()
  const applePayEntryData = useApplePayEntryData()
  const dispatch = useAppDispatch()
  const isNewFlowEnabled = useFlag(Features.DEFER_CHECKOUT_FOR_AMOUNT_ADJUSTMENT, false)
  const { doStartCheckoutAndHandleError } = useStartCheckout()
  const postLoginRoute = usePostLoginRoute()

  const handleOnChange = (value: string): void => {
    setAmount(value)
    const validationError = getValidationError(value, t, locale)
    setError(validationError)
  }

  const handleContinue = async (): Promise<void> => {
    logEvent(TrackingEvent.PRESSED_ADJUST_AMOUNT_PAGE_CONTINUE_BUTTON)

    if (applePayEntryData?.flowType !== ApplePaylaterFlowType.InStore && applePayEntryData?.flowType !== ApplePaylaterFlowType.WalletProvisioning) {
      console.error('Invalid flow type:', applePayEntryData?.flowType)
      return
    }

    if (isNullOrUndefined(applePayEntryData?.sessionIdentifier)) {
      console.error('Missing session data:', applePayEntryData)
      return
    }

    const isWalletProvisioning = applePayEntryData.flowType === ApplePaylaterFlowType.WalletProvisioning

    try {
      if (isNewFlowEnabled && isWalletProvisioning) {
        // WalletProvisioning defer flow: adjust the $1 checkout with real amount
        await createCheckout({
          sessionId: applePayEntryData.sessionIdentifier,
          ...(applePayEntryData.verifier != null ? { verifier: applePayEntryData.verifier } : {}),
          checkoutAmount: {
            amount,
            currency: mapLocaleToCurrency(locale),
          },
          isCheckoutAdjustment: true,
        }).unwrap()

        await doStartCheckoutAndHandleError()
        router.replace(postLoginRoute)
      } else {
        // InStore flow (and WalletProvisioning without flag): single createCheckout with real amount
        const response = await createCheckout({
          sessionId: applePayEntryData.sessionIdentifier,
          ...(applePayEntryData.verifier != null ? { verifier: applePayEntryData.verifier } : {}),
          checkoutAmount: {
            amount,
            currency: mapLocaleToCurrency(locale),
          },
        }).unwrap()

        const isValidCreateCheckoutResponse = validateCreateApplePayCheckoutResponse(response)

        if (!isValidCreateCheckoutResponse) {
          console.error('Create Apple Pay checkout response validation failed')
          router.replace(Route.TERMINAL_ERROR)
          return
        }

        const { redirectCheckoutUrl } = response
        const url = new URL(redirectCheckoutUrl)
        const queryParams: Record<string, string> = {}
        url.searchParams.forEach((value, key) => {
          queryParams[key] = value
        })

        queryParams.country = Country.US

        const hasInstoreCard = parseHasInstoreCard(queryParams[APPLE_PAYLATER_QUERY_PARAMS.USER_HAS_INSTORE_CARD])
        dispatch(setUserHasApplePaylaterCard(queryParams[APPLE_PAYLATER_QUERY_PARAMS.USER_HAS_APPLE_PAYLATER_CARD] === 'true'))
        dispatch(setUserHasInstoreCard(hasInstoreCard))

        router.replaceQuery({ pathname: getNextApplePaylaterRoute(hasInstoreCard), query: queryParams })
      }
    } catch (error) {
      console.error('Failed to create checkout:', error)
      router.replace(Route.TERMINAL_ERROR)
    }
  }

  return (
    <div className={styles.contentWrapper}>
      <div className={styles.headerSection}>
        <Heading size='XL'>{t('adjustAmount:heading')}</Heading>
        <Text size='M'>{t('adjustAmount:subtitle')}</Text>
      </div>
      <div className={styles.inputSection}>
        <div className={styles.centerInputMoneyContainer}>
          <div className={styles.inputMoneyContainer} data-testid='input-money-container'>
            <InputMoney
              bold
              value={amount}
              onChange={handleOnChange}
              locale={locale}
              maxDigits={MAX_DIGITS}
              testNameSpace='adjust-amount'
              variant={error ? 'error' : undefined}
            />
          </div>
        </div>
        {!isUndefinedOrEmpty(error) && (
          <Text size='XS' color='Error'>
            {error}
          </Text>
        )}
      </div>
      <div className={styles.ctaSection}>
        <Button
          onClick={() => {
            void handleContinue()
          }}
          disabled={isLoading || isSuccess || !isUndefinedOrEmpty(error) || isUndefinedOrEmpty(amount) || parseFloat(amount) === 0}
        >
          {t('common:continue')}
        </Button>
      </div>
    </div>
  )
}
